import { useTranslation } from 'react-i18next';
import { BadgeCheck, ExternalLink, MoreHorizontal, Pencil, Power, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RelativeTime, SectionCard, TablePagination } from '../../../../components/design-system/admin';
import { Vendor } from '../models/vendor.model';
import { ROWS_PER_PAGE_OPTIONS, VendorLogo, VendorStatus, isVendorEnabled } from './vendor-row-parts.component';

interface ProspectsTableProps {
    vendors: Vendor[];
    /** The published demo of each prospect that has one, by vendor id. */
    demoUrls: Record<string, string>;
    loading: boolean;
    pagination: { page: number; limit: number; total: number };
    onEdit: (vendor: Vendor) => void;
    onConvert: (vendor: Vendor) => void;
    onToggleStatus: (vendor: Vendor) => void;
    onDelete: (vendor: Vendor) => void;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (limit: number) => void;
}

/**
 * Prospects: the vendors the demo generator makes, one per demo. Listed apart
 * from customers, each with its demo, until it signs and is converted.
 */
export const ProspectsTable = ({
    vendors, demoUrls, loading, pagination, onEdit, onConvert, onToggleStatus, onDelete, onPageChange, onRowsPerPageChange,
}: ProspectsTableProps) => {
    const { t } = useTranslation();
    return (
        <SectionCard title={t('ADMIN_VENDORS.PROSPECTS.TITLE')} description={t('ADMIN_VENDORS.PROSPECTS.DESCRIPTION')} flush testId="prospects-table">
            <div className="overflow-x-auto border-t" aria-busy={loading}>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t('ADMIN_VENDORS.PROSPECTS.COL_NAME')}</TableHead>
                            <TableHead>{t('ADMIN_VENDORS.PROSPECTS.COL_DEMO')}</TableHead>
                            <TableHead>{t('ADMIN_VENDORS.PROSPECTS.COL_STATUS')}</TableHead>
                            <TableHead>{t('ADMIN_VENDORS.PROSPECTS.COL_LOGIN')}</TableHead>
                            <TableHead>{t('ADMIN_VENDORS.PROSPECTS.COL_CREATED')}</TableHead>
                            <TableHead className="w-12" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading
                            ? Array.from({ length: 3 }).map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell colSpan={6}><Skeleton className="h-6 w-full" /></TableCell>
                                </TableRow>
                            ))
                            : vendors.map((vendor) => {
                                const demo = demoUrls[vendor._id];
                                return (
                                    <TableRow key={vendor._id} data-testid={`prospect-row-${vendor.alias}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <VendorLogo vendor={vendor} className="size-9" />
                                                <div className="min-w-0">
                                                    <div className="truncate font-medium">{vendor.name}</div>
                                                    {vendor.storeUrl && <div className="truncate text-xs text-muted-foreground">{vendor.storeUrl}</div>}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {demo ? (
                                                <a href={demo} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm hover:underline">
                                                    {demo.replace(/^https:\/\//, '')}
                                                    <ExternalLink className="size-3.5" />
                                                </a>
                                            ) : '—'}
                                        </TableCell>
                                        <TableCell><VendorStatus vendor={vendor} /></TableCell>
                                        <TableCell className="text-sm text-muted-foreground">{vendor.email || '—'}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {vendor.createdAt ? <RelativeTime value={vendor.createdAt} /> : '—'}
                                        </TableCell>
                                        <TableCell className="text-end">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon-sm" aria-label={t('ADMIN_VENDORS.MENU.ACTIONS_FOR', { name: vendor.name })}>
                                                        <MoreHorizontal className="size-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="min-w-52">
                                                    {demo && (
                                                        <DropdownMenuItem onClick={() => window.open(demo, '_blank', 'noopener')}>
                                                            <ExternalLink className="size-4" />
                                                            {t('ADMIN_VENDORS.PROSPECTS.OPEN_DEMO')}
                                                        </DropdownMenuItem>
                                                    )}
                                                    <DropdownMenuItem onClick={() => onEdit(vendor)}>
                                                        <Pencil className="size-4" />
                                                        {t('ADMIN_VENDORS.MENU.EDIT')}
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => onConvert(vendor)}>
                                                        <BadgeCheck className="size-4" />
                                                        {t('ADMIN_VENDORS.PROSPECTS.CONVERT')}
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => onToggleStatus(vendor)}>
                                                        <Power className="size-4" />
                                                        {isVendorEnabled(vendor) ? t('ADMIN_VENDORS.MENU.DISABLE') : t('ADMIN_VENDORS.MENU.ENABLE')}
                                                    </DropdownMenuItem>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem variant="destructive" onClick={() => onDelete(vendor)}>
                                                        <Trash2 className="size-4" />
                                                        {t('ADMIN_VENDORS.MENU.DELETE')}
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </div>
            <TablePagination
                className="border-t"
                page={Math.max(0, (pagination?.page || 1) - 1)}
                pageSize={pagination?.limit || 10}
                total={pagination?.total || 0}
                onPageChange={onPageChange}
                onPageSizeChange={onRowsPerPageChange}
                pageSizes={ROWS_PER_PAGE_OPTIONS}
            />
        </SectionCard>
    );
};
